/**
 * Per-archetype color palette used by all four VibeLine modal steps.
 *
 * Step 1 (Energy) renders a 56×56 gradient circle inside each chip:
 *   linear-gradient(135deg, secondary 0%, primary 100%)
 * Steps 2 / 3 / 4 use only `primary` as the row / chip border color,
 * cycled through `PALETTE_ORDER` so the visual rhythm of the design
 * is preserved even when an option set is shorter or longer than 8.
 *
 * Values are taken directly from Figma node `3663:104331` (see plan file).
 * Some entries are numerically equal to theme tokens (`$pink-auxiliary`,
 * `$pink-auxiliary-light`, `$purple-30`) but the archetype palette is a
 * design language specific to this modal, not part of the global brand
 * token set — the overlap is incidental.  Co-located here rather than
 * promoted to `lib/theme/web-app/_variables.scss` so a future brand-token
 * change doesn't silently shift the modal's archetype colors.
 *
 * Consumed at runtime as inline `style={{ '--vibe-chip-border-color': … }}`
 * because Gaia's `Button` declares `style: PropTypes.string` (which would
 * warn on a real CSS-properties object). TODO: once `Button`'s PropType is
 * fixed (or it migrates to TypeScript), pass `style` directly and drop the
 * CSS-custom-property indirection in `VibeQuizModal.tsx`.
 */

export interface ArchetypePalette {
  /** Dark/saturated end. Used as gradient terminus + border color. */
  primary: string
  /** Light end of the step-1 gradient circle. */
  secondary: string
}

// Order matches ENERGY_OPTIONS in services/community/identity/profile/constants.ts.
export const ARCHETYPE_PALETTE: Record<string, ArchetypePalette> = {
  Integrated: { primary: '#ca4186', secondary: '#ffe7f6' },
  Inspired: { primary: '#8152a7', secondary: '#d8c0e7' },
  Intuitive: { primary: '#000ed5', secondary: '#9299ff' },
  Expressive: { primary: '#0278b3', secondary: '#dbf3ff' },
  Open: { primary: '#3f7d5a', secondary: '#c5ffce' },
  Confident: { primary: '#dfc800', secondary: '#fff8bd' },
  Creative: { primary: '#ff8a1d', secondary: '#ffe7c0' },
  Grounded: { primary: '#be2a2c', secondary: '#ff9193' },
}

/** Stable order — steps 2 / 3 / 4 cycle through this for per-row borders. */
export const PALETTE_ORDER: ReadonlyArray<string> = [
  'Integrated', 'Inspired', 'Intuitive', 'Expressive',
  'Open', 'Confident', 'Creative', 'Grounded',
]

/** Returns the palette entry at `i % PALETTE_ORDER.length`. */
export function colorForIndex (i: number): ArchetypePalette {
  return ARCHETYPE_PALETTE[PALETTE_ORDER[i % PALETTE_ORDER.length]]
}

/** Convenience: builds the 135deg gradient string used by the step-1 circle. */
export function archetypeGradient ({ primary, secondary }: ArchetypePalette): string {
  return `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`
}
